import React from 'react';

interface ModalWrapperProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** @deprecated Prefer `size` */
  maxWidth?: 'sm' | 'md' | 'lg';
  size?: 'sm' | 'md' | 'lg' | 'fullscreen';
  showCloseButton?: boolean;
  title?: string;
  subtitle?: string;
  /** Raise above another modal (e.g. transaction details over category drill-down).
   *  'elevated' sits above app overlays/animations (z-90..100) but below the
   *  blocking fullscreen flows (connect z-200, paywall z-300). */
  layer?: 'base' | 'stacked' | 'elevated';
}

const SIZE_CLASS_MAP = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  fullscreen: 'max-w-none sm:max-w-2xl h-[min(92dvh,calc(100dvh-0.5rem))] sm:h-[min(88dvh,calc(100dvh-2rem))]',
};

export const ModalWrapper: React.FC<ModalWrapperProps> = ({
  isOpen,
  onClose,
  children,
  maxWidth,
  size,
  showCloseButton,
  title,
  subtitle,
  layer = 'base',
}) => {
  if (!isOpen) return null;

  const resolvedSize = size ?? maxWidth ?? 'md';
  const isFullscreen = resolvedSize === 'fullscreen';
  const zClass = layer === 'elevated' ? 'z-[150]' : layer === 'stacked' ? 'z-90' : 'z-80';

  return (
    <div
      className={`fixed inset-0 ${zClass} flex justify-center bg-black/30 backdrop-blur-xs ${
        isFullscreen ? 'items-end p-0 sm:items-center sm:p-2' : 'items-end sm:items-center sm:p-4'
      }`}
      onClick={onClose}
    >
      <div
        className={`bg-cloud ${zClass} shadow-xl w-full flex flex-col pb-[env(safe-area-inset-bottom)] sm:pb-0 ${
          isFullscreen ? 'rounded-none sm:rounded-2xl pt-[env(safe-area-inset-top)]' : 'rounded-t-2xl sm:rounded-2xl'
        } ${SIZE_CLASS_MAP[resolvedSize]}`}
        style={isFullscreen ? undefined : { maxHeight: 'calc(100dvh - var(--sat, 0px) - 1rem)' }}
        onClick={e => e.stopPropagation()}
      >
        {(title || showCloseButton) && (
          <div
            className={`flex shrink-0 ${
              title ? 'items-start justify-between p-5 pb-0' : 'items-center justify-end px-4 pt-2 pb-0'
            }`}
          >
            <div>
              {title && <h3 className="text-lg font-serif text-growth">{title}</h3>}
              {subtitle && <p className="text-xs text-growth mt-1">{subtitle}</p>}
            </div>
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="p-2 -mr-1 text-growth/50 hover:text-growth transition-colors touch-manipulation"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        )}

        <div className={`flex-1 overflow-y-auto overscroll-contain scrollbar-hide no-scrollbar ${
          isFullscreen ? 'px-4 py-3 pb-6 min-h-0' : 'p-5 pb-20'
        }`}>
          {children}
        </div>
      </div>
    </div>
  );
};
